// React
import { useEffect, useMemo, useRef, useState } from "react";

// Redux
import { useSelector } from "react-redux";

// MUI
import { RootState } from "@/redux/store";
import { GridFilterModel } from "@mui/x-data-grid-premium";

// Context
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";

// Hooks
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

// API
import { onExportCitActivities, onUpdateCit, onUpdateCitActivities } from "@DataPages/api/financeManagement/cit";
import { onFinanceDealsUpdated } from "@DataPages/api/financeManagement/financeDeal/subscriptions";
import { onUpdateTradeInPayOffs } from "@DataPages/api/financeManagement/tradeInPayOffs/subscriptions.ts";

// Components
import BodyLayer from "@Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@Layouts/components/CollapsibleLeftPanel";
import PayPeriodSideBar from "@Layouts/components/SideBars/PayPeriodSideBar";
import FnIModel from "./FniModel";

// Types
import { IPeriodTreeSelection } from "@/Layouts/components/SideBars/PayPeriodSideBar/interfaces";

const QUERY_IDENTIFIERS = ["contractsInTransit", "citSystemSettings"];

const isCITUpdatable = (periodSelected: IPeriodTreeSelection) =>
	Boolean(periodSelected?.cycleStatus === "CLOSING" || periodSelected?.cycleStatus === "ACTIVE");

export default function FnILayer() {
	const { clearCache } = useCacheCleaner();
	const lastSelected = useRef<IPeriodTreeSelection>(null);
	const open = useRef(true);
	const [selection, setSelection] = useState<IPeriodTreeSelection>(null);
	const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const locationId = useSelector((state: RootState) => state?.location.location?.locationId);

	const sideBar = useMemo(() => {
		const sideSelection = function (selected?: IPeriodTreeSelection, isActualCycle = false) {
			if (selected?._id !== lastSelected.current?._id) {
				lastSelected.current = selected;
				setSelection(selected);
				if (isActualCycle) {
					addSubscription(
						["contractsInTransit", selected?._id],
						[
							{
								context: "onUpdateCit",
								query: onUpdateCit,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onUpdateCitActivities",
								query: onUpdateCitActivities,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
							{
								context: "onExportCitActivities",
								query: onExportCitActivities,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
						]
					);
					addSubscription(
						["financeDeal", selected?._id],
						[
							{
								context: "onFinanceDealsUpdated",
								query: onFinanceDealsUpdated,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
						]
					);
					addSubscription(
						["payoffs", selected?._id],
						[
							{
								context: "onUpdateTradeInPayOffs",
								query: onUpdateTradeInPayOffs,
								variables: {
									commissionCycleId: selected?.cycleId,
									payPeriodId: selected?.payPeriodId,
								},
							},
						]
					);
				}
			}
		};

		return <PayPeriodSideBar callbackSelected={sideSelection} selectDefaultLastCommissionCycle />;
	}, [addSubscription]);

	useEffect(() => {
		if (!locationId) {
			return () => {};
		}

		return () => {
			lastSelected.current = null;
			open.current = true;
			setSelection(null);
			clearCache(QUERY_IDENTIFIERS);
			clearSubscriptionContext();
		};
	}, [locationId, clearSubscriptionContext]);

	return (
		<BodyLayer floatingContent={<CollapsibleLeftPanel permanentTrigger={open.current} control={sideBar} />}>
			<FnIModel
				selectedPayPeriod={selection}
				dataPresent={Boolean(selection)}
				isUpdatable={isCITUpdatable(selection)}
				filterModel={filterModel}
				setFilterModel={setFilterModel}
			/>
		</BodyLayer>
	);
}
